import { useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  Loader2, Upload, RefreshCw, Search, Monitor, EyeOff, Eye, PauseCircle, PlayCircle, Eraser, Trash2, Download,
} from 'lucide-react'
import {
  useMdmApps, useSendMdmCommand, useUploadMdmFile, useMdmCatalogApps, useInstallMdmCatalogApp,
} from '../../../hooks/useMdm'
import ConfirmModal from '../../../components/Modal/ConfirmModal'
import { formatDateTime } from '../../../utils/format'
import { errorMessage } from './mobileUtils'

export default function AppsTab({ deviceId, state, readOnly }) {
  const { data, isLoading, error } = useMdmApps(deviceId)
  const cmd = useSendMdmCommand(deviceId)
  const upload = useUploadMdmFile(deviceId)
  const fileRef = useRef(null)
  const { data: catalog = [] } = useMdmCatalogApps(!readOnly)
  const installCatalog = useInstallMdmCatalogApp()
  const [catalogAppId, setCatalogAppId] = useState('')
  const [catalogKiosk, setCatalogKiosk] = useState(false)

  const [search, setSearch] = useState('')
  const [showSystem, setShowSystem] = useState(false)
  const [progress, setProgress] = useState(null)
  const [msg, setMsg] = useState(null)
  const [confirm, setConfirm] = useState(null) // { title, message, type, payload }

  const apps = useMemo(() => {
    const list = data?.apps || []
    const q = search.trim().toLowerCase()
    return list
      .filter((a) => showSystem || !a.system)
      .filter((a) => !q || String(a.label || '').toLowerCase().includes(q) || String(a.packageName).toLowerCase().includes(q))
      .sort((a, b) => String(a.label || a.packageName).localeCompare(String(b.label || b.packageName)))
  }, [data, search, showSystem])

  const kioskPackages = state?.policy?.kiosk?.packages || []

  const send = async (type, payload, okText) => {
    setMsg(null)
    try {
      await cmd.mutateAsync({ type, payload })
      setMsg({ ok: true, text: okText || 'Comando enviado.' })
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e) })
    }
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.apk')) {
      setMsg({ ok: false, text: 'Selecione um arquivo .apk.' })
      return
    }
    setMsg(null)
    setProgress(0)
    try {
      const uploaded = await upload.mutateAsync({ file, onProgress: setProgress })
      await cmd.mutateAsync({ type: 'install-apk', payload: { fileId: uploaded.id ?? uploaded.fileId } })
      setMsg({ ok: true, text: `"${file.name}" enviado — instalação silenciosa solicitada ao tablet.` })
    } catch (err) {
      setMsg({ ok: false, text: errorMessage(err, 'Falha ao enviar o APK.') })
    } finally {
      setProgress(null)
    }
  }

  const handleCatalogInstall = async () => {
    const app = catalog.find((a) => String(a.id) === catalogAppId)
    if (!app) return
    setMsg(null)
    try {
      await installCatalog.mutateAsync({ appId: app.id, deviceIds: [deviceId], kiosk: catalogKiosk })
      setMsg({
        ok: true,
        text: `Instalação de ${app.label || app.packageName} solicitada ao tablet${catalogKiosk ? ' — o quiosque começa quando terminar' : ''}.`,
      })
      setCatalogAppId('')
      setCatalogKiosk(false)
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, 'Falha ao solicitar a instalação.') })
    }
  }

  const busy = cmd.isPending || upload.isPending || installCatalog.isPending

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar app ou pacote"
            className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm w-64"
          />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={showSystem} onChange={(e) => setShowSystem(e.target.checked)} />
          Mostrar apps do sistema
        </label>
        <div className="flex-1" />
        <span className="text-xs text-gray-500">{data?.updatedAt ? `Inventário de ${formatDateTime(data.updatedAt)}` : 'Inventário ainda não recebido'}</span>
        <button
          type="button"
          onClick={() => send('sync-apps', null, 'Pedido de inventário enviado.')}
          disabled={busy || readOnly}
          className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className="w-4 h-4" /> Atualizar lista
        </button>
        {!readOnly && (
          <>
            <input ref={fileRef} type="file" accept=".apk,application/vnd.android.package-archive" className="hidden" onChange={handleFile} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90 disabled:opacity-50"
            >
              {upload.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {progress != null ? `Enviando ${progress}%` : 'Instalar APK'}
            </button>
          </>
        )}
      </div>

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
          <span className="font-medium text-gray-700">Instalar do catálogo:</span>
          <select
            value={catalogAppId}
            onChange={(e) => setCatalogAppId(e.target.value)}
            disabled={busy || catalog.length === 0}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm min-w-[14rem] bg-white"
          >
            <option value="">{catalog.length === 0 ? 'Catálogo vazio (aba Mobile › Apps gerenciados)' : 'Selecione um app…'}</option>
            {catalog.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {(a.label || a.packageName)} — {a.versionName || '?'} ({a.versionCode})
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-gray-600">
            <input type="checkbox" checked={catalogKiosk} onChange={(e) => setCatalogKiosk(e.target.checked)} disabled={busy} />
            Definir como quiosque
          </label>
          <button
            type="button"
            onClick={handleCatalogInstall}
            disabled={!catalogAppId || busy}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 bg-white rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {installCatalog.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Instalar
          </button>
        </div>
      )}

      {msg && <p className={clsx('text-sm', msg.ok ? 'text-green-700' : 'text-red-600')}>{msg.text}</p>}
      {error && <p className="text-sm text-red-600">{errorMessage(error, 'Erro ao carregar os apps.')}</p>}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">App</th>
              <th className="text-left px-3 py-2">Versão</th>
              <th className="text-left px-3 py-2">Estado</th>
              {!readOnly && <th className="text-right px-3 py-2">Ações</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando...</td></tr>
            ) : apps.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-500">Nenhum app encontrado.</td></tr>
            ) : apps.map((a) => {
              const inKiosk = kioskPackages.includes(a.packageName)
              return (
                <tr key={a.packageName} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{a.label || a.packageName}</div>
                    <div className="text-xs text-gray-500 font-mono">{a.packageName}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{a.versionName || '—'}{a.versionCode != null ? <span className="text-xs text-gray-400"> ({a.versionCode})</span> : null}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {a.system && <Tag className="bg-gray-100 text-gray-600">sistema</Tag>}
                      {a.enabled === false && <Tag className="bg-gray-100 text-gray-600">desativado</Tag>}
                      {a.hidden && <Tag className="bg-amber-100 text-amber-800">oculto</Tag>}
                      {a.suspended && <Tag className="bg-red-100 text-red-700">suspenso</Tag>}
                      {inKiosk && <Tag className="bg-indigo-100 text-indigo-700">quiosque</Tag>}
                    </div>
                  </td>
                  {!readOnly && (
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <IconBtn title="Abrir em modo quiosque" icon={Monitor} disabled={busy}
                          onClick={() => send('kiosk-start', { packageName: a.packageName }, `Quiosque com ${a.label || a.packageName} solicitado.`)} />
                        <IconBtn title={a.hidden ? 'Mostrar' : 'Ocultar'} icon={a.hidden ? Eye : EyeOff} disabled={busy}
                          onClick={() => send('set-app-hidden', { packageName: a.packageName, hidden: !a.hidden })} />
                        <IconBtn title={a.suspended ? 'Liberar' : 'Suspender'} icon={a.suspended ? PlayCircle : PauseCircle} disabled={busy}
                          onClick={() => send('set-app-suspended', { packageName: a.packageName, suspended: !a.suspended })} />
                        <IconBtn title="Limpar dados" icon={Eraser} disabled={busy}
                          onClick={() => setConfirm({ title: 'Limpar dados do app', message: `Apagar todos os dados de ${a.label || a.packageName}?`, type: 'clear-app-data', payload: { packageName: a.packageName } })} />
                        {!a.system && (
                          <IconBtn title="Desinstalar" icon={Trash2} danger disabled={busy}
                            onClick={() => setConfirm({ title: 'Desinstalar app', message: `Desinstalar ${a.label || a.packageName} do tablet?`, type: 'uninstall-app', payload: { packageName: a.packageName } })} />
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title}
        message={confirm?.message}
        confirmText="Confirmar"
        cancelText="Cancelar"
        onConfirm={() => confirm && send(confirm.type, confirm.payload)}
      />
    </div>
  )
}

function Tag({ className, children }) {
  return <span className={clsx('px-1.5 py-0.5 rounded text-xs', className)}>{children}</span>
}

function IconBtn({ icon: Icon, title, onClick, disabled, danger }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={clsx('p-1.5 rounded-md border border-transparent hover:border-gray-200 disabled:opacity-40',
        danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-600 hover:bg-gray-100')}
    >
      <Icon className="w-4 h-4" />
    </button>
  )
}
